import React, { useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import Layout from "../components/Layout";
import RouteMap from "../components/RouteMap";
import { API_BASE_URL } from "../constants/api";
import { MdUploadFile, MdError, MdDownload, MdRefresh } from "react-icons/md";

const tableStyles = {
  table:   { style: { backgroundColor: "#1c1c1c" } },
  headRow: { style: { backgroundColor: "#2a2a2a", color: "#ffffff", fontSize: "13px", fontWeight: "600", borderBottom: "1px solid #333333" } },
  rows:    { style: { backgroundColor: "#1c1c1c", color: "#dddddd", borderBottom: "1px solid #222222", "&:hover": { backgroundColor: "#252525" } } },
  pagination: { style: { backgroundColor: "#1c1c1c", color: "#aaaaaa", borderTop: "1px solid #2a2a2a" } },
};

// Parses a timestamp as UTC when it has no explicit timezone designator.
// server_ts arrives as a plain UTC string with no zone info, in either
// "YYYY-MM-DD HH:mm:ss" or "DD-MM-YYYY HH:mm:ss" form.
const parseAsUtcDate = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();

  const dmy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})$/);
  if (dmy) {
    const [, dd, mm, yyyy, hh, min, ss] = dmy;
    const d = new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}Z`);
    return isNaN(d.getTime()) ? null : d;
  }

  const hasTzInfo = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed);
  const isoLike = hasTzInfo ? trimmed : `${trimmed.replace(" ", "T")}Z`;
  const d = new Date(isoLike);
  return isNaN(d.getTime()) ? null : d;
};

// Displays a timestamp in IST (UTC+5:30) for on-screen use.
const formatIst = (value) => {
  const d = parseAsUtcDate(value);
  if (!d) return value || "N/A";
  const formatted = d.toLocaleString("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  return `${formatted.replace(",", "")} IST`;
};

const csvField = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Ignition is decoded from two independent signals — CAN 0xC2 byte 6 and the TCU eventFlag bit.
// On real device data byte 6 has been observed stuck at 0 for hours of genuine riding, so trip
// detection trusts EITHER signal indicating "on"; canIgnitionMismatch flags rows where eventFlag
// says the vehicle is on but byte 6 fails to reflect it, so that can be monitored/investigated.
const isIgnitionOnCan = (row) => row.ignitionStatus !== null && row.ignitionStatus !== undefined && row.ignitionStatus !== 0;
const isIgnitionOnEventFlag = (row) => row.ignitionEventFlag === 1;
const hasIgnitionSignal = (row) =>
  (row.ignitionStatus !== null && row.ignitionStatus !== undefined) ||
  (row.ignitionEventFlag !== null && row.ignitionEventFlag !== undefined);
const isIgnitionOnCombined = (row) => isIgnitionOnCan(row) || isIgnitionOnEventFlag(row);
const isCanIgnitionMismatch = (row) => isIgnitionOnEventFlag(row) && !isIgnitionOnCan(row);

const TIME_FIELDS = {
  tcu: "tcuPacketTimeUtc",
  server: "serverTs",
};

const TIME_LABELS = {
  tcu: "TCU Time",
  server: "Server Time",
};

const sortDecodedRowsByTime = (rows, sortBy) => {
  const timeField = TIME_FIELDS[sortBy];
  if (!rows || !timeField) return [];

  return rows
    .filter((row) => row.parseStatus === "OK" && parseAsUtcDate(row[timeField]))
    .slice()
    .sort((a, b) => {
      const timeDifference = parseAsUtcDate(a[timeField]).getTime() - parseAsUtcDate(b[timeField]).getTime();
      return timeDifference || (a.rowNumber ?? 0) - (b.rowNumber ?? 0);
    });
};

const PacketDecodePage = () => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [errorFile, setErrorFile] = useState(null);
  const [errorDragging, setErrorDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState(null);
  const [errorRows, setErrorRows] = useState(null);
  const [socThreshold, setSocThreshold] = useState(5);
  const [odoThreshold, setOdoThreshold] = useState(2);
  const [gapThresholdMin, setGapThresholdMin] = useState(20);
  const [minTripDistanceKm, setMinTripDistanceKm] = useState(0.1);
  const [minTripDurationSec, setMinTripDurationSec] = useState(30);
  const [packetLossSortBy, setPacketLossSortBy] = useState("tcu");
  const [packetLossSortByOpp, setPacketLossSortByOpp] = useState("server");
  const [rideSortBy, setRideSortBy] = useState("tcu");
  const [rideSortByOpp, setRideSortByOpp] = useState("server");
  const [selectedTripKey, setSelectedTripKey] = useState(null);
  const fileInputRef = useRef(null);
  const errorFileInputRef = useRef(null);

  const pickFile = (f) => {
    if (!f || !f.name.endsWith(".csv")) {
      setError("Please select a .csv file.");
      return;
    }
    setFile(f);
    setError(null);
  };

  const pickErrorFile = (f) => {
    if (!f || !f.name.endsWith(".csv")) {
      setError("Please select a .csv file for the error packets CSV.");
      return;
    }
    setErrorFile(f);
    setError(null);
  };

  const onFileChange = (e) => pickFile(e.target.files[0]);
  const onErrorFileChange = (e) => pickErrorFile(e.target.files[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  };

  const onErrorDrop = (e) => {
    e.preventDefault();
    setErrorDragging(false);
    pickErrorFile(e.dataTransfer.files[0]);
  };

  const postCsv = async (endpoint, csvFile) => {
    const formData = new FormData();
    formData.append("file", csvFile);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, { method: "POST", body: formData });
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || "Upload failed");
    }
    return res.json();
  };

  const handleUpload = async () => {
    if (!file) { setError("Please select a CSV file first."); return; }

    setUploading(true);
    setError(null);
    setRows(null);
    setErrorRows(null);

    try {
      const [data, errorData] = await Promise.all([
        postCsv("/api/packet-decode", file),
        errorFile ? postCsv("/api/packet-decode/errors", errorFile) : Promise.resolve(null),
      ]);
      setRows(data);
      setErrorRows(errorData);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setErrorFile(null);
    setRows(null);
    setErrorRows(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (errorFileInputRef.current) errorFileInputRef.current.value = "";
  };

  const packetLossRows = useMemo(
    () => sortDecodedRowsByTime(rows, packetLossSortBy),
    [rows, packetLossSortBy]
  );
  const rideRows = useMemo(
    () => sortDecodedRowsByTime(rows, rideSortBy),
    [rows, rideSortBy]
  );

  // Error/rejected packets, sorted by TCU time — the only meaningful basis for asking
  // "did the TCU transmit during this gap", since these packets never received a real
  // server_ts (db_insert_time is just when the rejection was logged).
  const rejectedRowsByTcu = useMemo(
    () => sortDecodedRowsByTime(errorRows, "tcu"),
    [errorRows]
  );

  // A 0 reading is treated as missing data, not a real value — null it out so the
  // line draws through the gap (connectNulls) instead of dipping to 0.
  const isValidReading = (value) => value !== null && value !== undefined && value !== 0;

  const chartData = useMemo(() => {
    const timeField = TIME_FIELDS[packetLossSortBy];
    return packetLossRows.map((r) => ({
      time: formatIst(r[timeField]),
      soc: isValidReading(r.soc) ? r.soc : null,
      odometerKm: isValidReading(r.odometerKm) ? r.odometerKm : null,
    }));
  }, [packetLossRows, packetLossSortBy]);

  // Packet loss = an implausible jump between two consecutive VALID (non-zero) readings of
  // the same metric. Odometer can only increase (any decrease always violates); SoC can move
  // either direction but only violates past the configurable threshold.
  const packetLossEvents = useMemo(() => {
    if (packetLossRows.length < 2) return [];

    const socViolations = [];
    const odoViolations = [];
    let lastValidSoc = null; // { value, idx }
    let lastValidOdo = null;

    packetLossRows.forEach((row, idx) => {
      if (isValidReading(row.soc)) {
        if (lastValidSoc && Math.abs(row.soc - lastValidSoc.value) > socThreshold) {
          socViolations.push({ startIdx: lastValidSoc.idx, endIdx: idx });
        }
        lastValidSoc = { value: row.soc, idx };
      }

      if (isValidReading(row.odometerKm)) {
        if (lastValidOdo) {
          const decreased = row.odometerKm < lastValidOdo.value;
          const jumped = (row.odometerKm - lastValidOdo.value) > odoThreshold;
          if (decreased || jumped) {
            odoViolations.push({ startIdx: lastValidOdo.idx, endIdx: idx });
          }
        }
        lastValidOdo = { value: row.odometerKm, idx };
      }
    });

    const tagged = [
      ...socViolations.map((v) => ({ ...v, metrics: new Set(["SoC"]) })),
      ...odoViolations.map((v) => ({ ...v, metrics: new Set(["Odometer"]) })),
    ].sort((a, b) => a.startIdx - b.startIdx || a.endIdx - b.endIdx);

    // Merge overlapping/touching [startIdx, endIdx] row ranges into single events.
    const merged = [];
    tagged.forEach((violation) => {
      const last = merged[merged.length - 1];
      if (last && violation.startIdx <= last.endIdx) {
        last.endIdx = Math.max(last.endIdx, violation.endIdx);
        violation.metrics.forEach((m) => last.metrics.add(m));
      } else {
        merged.push({ startIdx: violation.startIdx, endIdx: violation.endIdx, metrics: new Set(violation.metrics) });
      }
    });

    return merged.map((event) => {
      const startRow = packetLossRows[event.startIdx];
      const endRow = packetLossRows[event.endIdx];
      const socStart = isValidReading(startRow.soc) ? startRow.soc : null;
      const socEnd = isValidReading(endRow.soc) ? endRow.soc : null;
      const odoStart = isValidReading(startRow.odometerKm) ? startRow.odometerKm : null;
      const odoEnd = isValidReading(endRow.odometerKm) ? endRow.odometerKm : null;

      // Rejected packets whose TCU time falls inside this event's TCU window indicate the
      // TCU did transmit during the gap — the server just failed to store those packets.
      const tcuStartMs = parseAsUtcDate(startRow.tcuPacketTimeUtc)?.getTime();
      const tcuEndMs = parseAsUtcDate(endRow.tcuPacketTimeUtc)?.getTime();
      const rejectedInGap = (tcuStartMs != null && tcuEndMs != null)
        ? rejectedRowsByTcu.filter((r) => {
            const ms = parseAsUtcDate(r.tcuPacketTimeUtc).getTime();
            return ms >= tcuStartMs && ms <= tcuEndMs;
          })
        : [];
      const rejectedExceptionMessages = Array.from(
        new Set(rejectedInGap.map((r) => r.exceptionMessage).filter(Boolean))
      );

      return {
        startTime: {
          tcu: formatIst(startRow.tcuPacketTimeUtc),
          server: formatIst(startRow.serverTs),
        },
        endTime: {
          tcu: formatIst(endRow.tcuPacketTimeUtc),
          server: formatIst(endRow.serverTs),
        },
        triggeredBy: Array.from(event.metrics).join(" + "),
        socStart,
        socEnd,
        socGap: (socStart !== null && socEnd !== null) ? socEnd - socStart : null,
        odoStart,
        odoEnd,
        odoGap: (odoStart !== null && odoEnd !== null) ? odoEnd - odoStart : null,
        rejectedCount: rejectedInGap.length,
        rejectedExceptionMessages,
      };
    });
  }, [packetLossRows, socThreshold, odoThreshold, rejectedRowsByTcu]);

  // Rides (trips): a trip starts on an ignition 0->1 edge and ends on 1->0 (or end of data), using
  // the COMBINED ignition signal (CAN 0xC2 byte 6 OR the TCU eventFlag bit — either one indicating
  // "on" counts, since byte 6 alone has been observed to never toggle on some real device data).
  // A data gap longer than gapThresholdMin while a trip is running force-ends it there (mirrors
  // the legacy getPathReportCANCASSANDRA_20230220 20-minute gap-split behavior). Candidate trips
  // shorter than the min distance/duration are discarded as noise.
  const trips = useMemo(() => {
    if (rideRows.length < 2) return [];

    const rideTimeField = TIME_FIELDS[rideSortBy];
    const events = [];

    const findValidForward = (key, startIdx, endIdx) => {
      for (let i = startIdx; i <= endIdx; i++) {
        if (isValidReading(rideRows[i][key])) return rideRows[i][key];
      }
      return null;
    };
    const findValidBackward = (key, startIdx, endIdx) => {
      for (let i = endIdx; i >= startIdx; i--) {
        if (isValidReading(rideRows[i][key])) return rideRows[i][key];
      }
      return null;
    };

    const endTrip = (startIdx, endIdx) => {
      if (endIdx <= startIdx) return;

      const startSoc = findValidForward("soc", startIdx, endIdx);
      const endSoc = findValidBackward("soc", startIdx, endIdx);
      const startOdo = findValidForward("odometerKm", startIdx, endIdx);
      const endOdo = findValidBackward("odometerKm", startIdx, endIdx);

      const startMs = parseAsUtcDate(rideRows[startIdx][rideTimeField]).getTime();
      const endMs = parseAsUtcDate(rideRows[endIdx][rideTimeField]).getTime();
      const durationSec = (endMs - startMs) / 1000;
      const distanceKm = (startOdo !== null && endOdo !== null) ? (endOdo - startOdo) : null;

      if (distanceKm === null || distanceKm < minTripDistanceKm || durationSec < minTripDurationSec) {
        return;
      }

      events.push({
        startIdx,
        endIdx,

        startTime: {
          tcu: formatIst(rideRows[startIdx].tcuPacketTimeUtc),
          server: formatIst(rideRows[startIdx].serverTs),
        },

        endTime: {
          tcu: formatIst(rideRows[endIdx].tcuPacketTimeUtc),
          server: formatIst(rideRows[endIdx].serverTs),
        },

        durationSec,
        distanceKm,
        startSoc,
        endSoc,
        startOdo,
        endOdo,
      });

    };

    let tripStartIdx = null;
    let lastKnownIgnition = false; // assume off before the data begins

    rideRows.forEach((row, idx) => {
      if (idx > 0 && tripStartIdx !== null) {
        const prevMs = parseAsUtcDate(rideRows[idx - 1][rideTimeField]).getTime();
        const curMs = parseAsUtcDate(row[rideTimeField]).getTime();
        if ((curMs - prevMs) / 60000 > gapThresholdMin) {
          endTrip(tripStartIdx, idx - 1);
          tripStartIdx = null;
          lastKnownIgnition = false; // presume ignition went off during the data-loss gap
        }
      }

      if (hasIgnitionSignal(row)) {
        const ignitionOn = isIgnitionOnCombined(row);
        if (!lastKnownIgnition && ignitionOn) {
          tripStartIdx = idx;
        } else if (lastKnownIgnition && !ignitionOn && tripStartIdx !== null) {
          endTrip(tripStartIdx, idx);
          tripStartIdx = null;
        }
        lastKnownIgnition = ignitionOn;
      }
    });

    if (tripStartIdx !== null) {
      endTrip(tripStartIdx, rideRows.length - 1);
    }

    return events;
  }, [rideRows, rideSortBy, gapThresholdMin, minTripDistanceKm, minTripDurationSec]);

  const tripKey = (t) => `${t.startIdx}-${t.endIdx}`;
  const selectedTrip = trips.find((t) => tripKey(t) === selectedTripKey) || null;

  const selectedTripRoutePoints = useMemo(() => {
    if (!selectedTrip) return [];
    return rideRows
      .slice(selectedTrip.startIdx, selectedTrip.endIdx + 1)
      .filter((r) => r.latitude != null && r.longitude != null && !(r.latitude === 0 && r.longitude === 0))
      .map((r, i, arr) => ({
        lat: r.latitude,
        lng: r.longitude,
        datetime: (i === 0 || i === arr.length - 1) ? formatIst(r[TIME_FIELDS[rideSortBy]]) : null,
      }));
  }, [selectedTrip, rideRows, rideSortBy]);

  const successCount = rows ? rows.filter((r) => r.parseStatus === "OK").length : 0;
  const errorCount = rows ? rows.length - successCount : 0;
  const canMismatchCount = rows ? rows.filter((r) => r.parseStatus === "OK" && isCanIgnitionMismatch(r)).length : 0;

  const columns = [
    { name: "Row #",     selector: (row) => row.rowNumber, sortable: true, width: "70px" },
    { name: "Server TS (IST)", selector: (row) => formatIst(row.serverTs), wrap: true },
    { name: "TCU Packet Time (IST)", selector: (row) => formatIst(row.tcuPacketTimeUtc), wrap: true },
    { name: "Unit ID",   selector: (row) => row.unitId || "N/A", wrap: true },
    { name: "Ignition (CAN)", selector: (row) => (row.ignitionStatus ?? "N/A"), width: "110px" },
    { name: "Ignition (EventFlag)", selector: (row) => (row.ignitionEventFlag ?? "N/A"), width: "140px" },
    {
      name: "CAN Mismatch",
      width: "120px",
      cell: (row) =>
        row.parseStatus === "OK" && isCanIgnitionMismatch(row) ? (
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ color: "#f59e0b", backgroundColor: "#f59e0b22" }}
            title="EventFlag indicates ignition on, but CAN 0xC2 byte 6 does not reflect it"
          >
            Mismatch
          </span>
        ) : null,
    },
    { name: "SoC (%)",   selector: (row) => (row.soc ?? "N/A"), width: "90px" },
    { name: "Odometer (km)", selector: (row) => (row.odometerKm ?? "N/A") },
    { name: "Charging",  selector: (row) => (row.chargingStatus === 1 ? "Charging" : row.chargingStatus === 0 ? "Not Charging" : "N/A"), width: "120px" },
    {
      name: "Status",
      width: "110px",
      cell: (row) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs font-semibold"
          style={
            row.parseStatus === "OK"
              ? { color: "#22c55e", backgroundColor: "#22c55e22" }
              : { color: "#ef4444", backgroundColor: "#ef444422" }
          }
          title={row.errorMessage || ""}
        >
          {row.parseStatus}
        </span>
      ),
    },
  ];

  const errorColumns = [
    { name: "Row #", selector: (row) => row.rowNumber, sortable: true, width: "70px" },
    { name: "TCU Packet Time (IST)", selector: (row) => formatIst(row.tcuPacketTimeUtc), wrap: true },
    { name: "DB Insert Time (IST)", selector: (row) => formatIst(row.serverTs), wrap: true },
    { name: "Exception Message", selector: (row) => row.exceptionMessage || "N/A", wrap: true },
    {
      name: "Decode Status",
      width: "120px",
      cell: (row) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs font-semibold"
          style={
            row.parseStatus === "OK"
              ? { color: "#22c55e", backgroundColor: "#22c55e22" }
              : { color: "#ef4444", backgroundColor: "#ef444422" }
          }
          title={row.errorMessage || ""}
        >
          {row.parseStatus}
        </span>
      ),
    },
  ];

  const fmtGap = (value, unit) => (value === null || value === undefined ? "-" : `${value > 0 ? "+" : ""}${value.toFixed(1)}${unit}`);
  const fmtVal = (value, unit) => (value === null || value === undefined ? "-" : `${value}${unit}`);

  const packetLossColumns = [
    {
      name: `Start ${TIME_LABELS[packetLossSortBy]} (IST)`,
      selector: (row) => row.startTime[packetLossSortBy],
      wrap: true,
    },
    {
      name: `End ${TIME_LABELS[packetLossSortBy]} (IST)`,
      selector: (row) => row.endTime[packetLossSortBy],
      wrap: true,
    },
    {
      name: `Start ${TIME_LABELS[packetLossSortByOpp]} (IST)`,
      selector: (row) => row.startTime[packetLossSortByOpp],
      wrap: true,
    },
    {
      name: `End ${TIME_LABELS[packetLossSortByOpp]} (IST)`,
      selector: (row) => row.endTime[packetLossSortByOpp],
      wrap: true,
    },
    { name: "Triggered By",     selector: (row) => row.triggeredBy, width: "130px" },
    { name: "SoC Start",  selector: (row) => fmtVal(row.socStart, "%"), width: "100px" },
    { name: "SoC End",    selector: (row) => fmtVal(row.socEnd, "%"), width: "100px" },
    { name: "SoC Gap",    selector: (row) => fmtGap(row.socGap, "%"), width: "100px" },
    { name: "Odo Start (km)", selector: (row) => fmtVal(row.odoStart, ""), width: "110px" },
    { name: "Odo End (km)",   selector: (row) => fmtVal(row.odoEnd, ""), width: "110px" },
    { name: "Odo Gap (km)",   selector: (row) => fmtGap(row.odoGap, ""), width: "110px" },
    {
      name: "Rejected Pkts In Gap",
      width: "150px",
      cell: (row) =>
        row.rejectedCount > 0 ? (
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ color: "#f59e0b", backgroundColor: "#f59e0b22" }}
            title={row.rejectedExceptionMessages.join(" | ")}
          >
            {row.rejectedCount}
          </span>
        ) : (
          <span style={{ color: "#555555" }}>0</span>
        ),
    },
  ];

  const fmtDuration = (sec) => {
    const s = Math.round(sec);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
  };

  const tripColumns = [
    { name: `Start ${TIME_LABELS[rideSortBy]} (IST)`, selector: (row) => row.startTime[rideSortBy], wrap: true },
    { name: `End ${TIME_LABELS[rideSortBy]} (IST)`, selector: (row) => row.endTime[rideSortBy], wrap: true },
    { name: `Start ${TIME_LABELS[rideSortByOpp]} (IST)`, selector: (row) => row.startTime[rideSortByOpp], wrap: true },
    { name: `End ${TIME_LABELS[rideSortByOpp]} (IST)`, selector: (row) => row.endTime[rideSortByOpp], wrap: true },
    { name: "Duration",  selector: (row) => fmtDuration(row.durationSec), width: "100px" },
    { name: "Distance (km)", selector: (row) => row.distanceKm.toFixed(2), width: "110px" },
    { name: "Start SoC", selector: (row) => fmtVal(row.startSoc, "%"), width: "100px" },
    { name: "End SoC",   selector: (row) => fmtVal(row.endSoc, "%"), width: "100px" },
    { name: "Start Odo (km)", selector: (row) => fmtVal(row.startOdo, ""), width: "110px" },
    { name: "End Odo (km)",   selector: (row) => fmtVal(row.endOdo, ""), width: "110px" },
  ];

  const downloadCSV = () => {
    if (!rows || !rows.length) return;
    const headers = [
      "row_number", "server_ts", "tcu_packet_time_utc", "packet_format",
      "unit_id", "ignition_status_can", "ignition_status_eventflag", "can_ignition_mismatch",
      "soc", "odometer_km", "charging_status", "parse_status", "error_message",
    ];
    const csvRows = [headers.join(",")];
    rows.forEach((row) => {
      csvRows.push([
        row.rowNumber,
        csvField(row.serverTs),
        csvField(row.tcuPacketTimeUtc),
        csvField(row.packetFormat),
        csvField(row.unitId),
        row.ignitionStatus ?? "",
        row.ignitionEventFlag ?? "",
        row.parseStatus === "OK" ? isCanIgnitionMismatch(row) : "",
        row.soc ?? "",
        row.odometerKm ?? "",
        row.chargingStatus ?? "",
        csvField(row.parseStatus),
        csvField(row.errorMessage),
      ].join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `packet-decode-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout title="Packet Decode">
      <div className="space-y-6">
        <p style={{ color: "#888888" }}>
          Upload a CSV with <code>server_ts</code> and <code>raw_data</code> columns (ASCII or
          HEXDUMP TCU packets). Each packet is decoded for SoC, ignition status, odometer and
          TCU packet time. Optionally, also upload the Error/Rejected Packets CSV
          (<code>packet</code>, <code>exception_message</code>, <code>db_insert_time</code>
          columns) to see packets the TCU sent that the server failed to store — these are
          correlated against detected packet loss gaps below.
        </p>

        {/* Upload card */}
        <div className="rounded-2xl p-8 space-y-6 max-w-2xl" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="rounded-xl flex flex-col items-center justify-center gap-3 py-10 cursor-pointer transition-colors"
            style={{
              border: `2px dashed ${dragging ? "#ffc20e" : "#2a2a2a"}`,
              backgroundColor: dragging ? "#ffc20e11" : "transparent",
            }}
          >
            <MdUploadFile className="text-4xl" style={{ color: file ? "#ffc20e" : "#555555" }} />
            {file ? (
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: "#ffffff" }}>{file.name}</p>
                <p className="text-xs mt-1" style={{ color: "#888888" }}>{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm" style={{ color: "#aaaaaa" }}>
                  Raw Packet CSV — drag &amp; drop, or <span style={{ color: "#ffc20e" }}>browse</span>
                </p>
                <p className="text-xs mt-1" style={{ color: "#555555" }}>Only .csv files accepted</p>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={onFileChange} />
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setErrorDragging(true); }}
            onDragLeave={() => setErrorDragging(false)}
            onDrop={onErrorDrop}
            onClick={() => !uploading && errorFileInputRef.current?.click()}
            className="rounded-xl flex flex-col items-center justify-center gap-3 py-6 cursor-pointer transition-colors"
            style={{
              border: `2px dashed ${errorDragging ? "#ffc20e" : "#2a2a2a"}`,
              backgroundColor: errorDragging ? "#ffc20e11" : "transparent",
            }}
          >
            <MdUploadFile className="text-3xl" style={{ color: errorFile ? "#ffc20e" : "#555555" }} />
            {errorFile ? (
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: "#ffffff" }}>{errorFile.name}</p>
                <p className="text-xs mt-1" style={{ color: "#888888" }}>{(errorFile.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm" style={{ color: "#aaaaaa" }}>
                  Error Packets CSV (optional) — drag &amp; drop, or <span style={{ color: "#ffc20e" }}>browse</span>
                </p>
                <p className="text-xs mt-1" style={{ color: "#555555" }}>Only .csv files accepted</p>
              </div>
            )}
            <input ref={errorFileInputRef} type="file" accept=".csv" className="hidden" onChange={onErrorFileChange} />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg px-4 py-3" style={{ backgroundColor: "#ef444422", border: "1px solid #ef444444" }}>
              <MdError style={{ color: "#ef4444", flexShrink: 0 }} />
              <p className="text-sm" style={{ color: "#ef4444" }}>{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleUpload}
              disabled={uploading || !file}
              className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-colors"
              style={{
                backgroundColor: "#ffc20e",
                color: "#000000",
                opacity: (uploading || !file) ? 0.5 : 1,
                cursor: (uploading || !file) ? "not-allowed" : "pointer",
              }}
            >
              {uploading ? "Decoding…" : "Decode"}
            </button>

            {(rows || file) && (
              <button
                onClick={handleReset}
                className="px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors flex items-center gap-2"
                style={{ border: "1px solid #2a2a2a", color: "#aaaaaa" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#2a2a2a"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <MdRefresh /> Reset
              </button>
            )}
          </div>
        </div>

        {rows && (
          <>
            {/* Summary + download */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-3">
                <div className="rounded-xl px-4 py-2 text-center" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                  <p className="text-lg font-bold" style={{ color: "#ffffff" }}>{rows.length}</p>
                  <p className="text-xs" style={{ color: "#555555" }}>Total</p>
                </div>
                <div className="rounded-xl px-4 py-2 text-center" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                  <p className="text-lg font-bold" style={{ color: "#22c55e" }}>{successCount}</p>
                  <p className="text-xs" style={{ color: "#555555" }}>Decoded</p>
                </div>
                <div className="rounded-xl px-4 py-2 text-center" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                  <p className="text-lg font-bold" style={{ color: errorCount > 0 ? "#ef4444" : "#555555" }}>{errorCount}</p>
                  <p className="text-xs" style={{ color: "#555555" }}>Failed</p>
                </div>
                <div
                  className="rounded-xl px-4 py-2 text-center"
                  style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}
                  title="Rows where the TCU eventFlag indicates ignition on, but CAN 0xC2 byte 6 does not reflect it"
                >
                  <p className="text-lg font-bold" style={{ color: canMismatchCount > 0 ? "#f59e0b" : "#555555" }}>{canMismatchCount}</p>
                  <p className="text-xs" style={{ color: "#555555" }}>CAN Mismatch</p>
                </div>
              </div>
              <button
                onClick={downloadCSV}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-colors"
                style={{ backgroundColor: "#ffc20e", color: "#000000" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e6ae00"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#ffc20e"; }}
              >
                <MdDownload /> Download CSV
              </button>
            </div>

            {/* Chart */}
            {successCount > 0 && (
              <div className="rounded-2xl p-6" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <p className="text-sm font-semibold" style={{ color: "#ffffff" }}>
                    SoC &amp; Odometer vs {packetLossSortBy === "tcu" ? "TCU Time" : "Server Time"} (IST)
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Packet loss sort by
                      <select
                          value={packetLossSortBy}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPacketLossSortBy(value);
                            setPacketLossSortByOpp(value === "tcu" ? "server" : "tcu");
                          }}
                          className="rounded-md px-2 py-1 text-sm"
                          style={{
                            backgroundColor: "#111111",
                            border: "1px solid #2a2a2a",
                            color: "#ffffff",
                          }}
                      >
                        <option value="tcu">TCU time</option>
                        <option value="server">Server time</option>
                      </select>

                    </label>
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Max SoC jump (%)
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={socThreshold}
                        onChange={(e) => setSocThreshold(Number(e.target.value) || 0)}
                        className="w-16 rounded-md px-2 py-1 text-sm"
                        style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Max Odometer jump (km)
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={odoThreshold}
                        onChange={(e) => setOdoThreshold(Number(e.target.value) || 0)}
                        className="w-16 rounded-md px-2 py-1 text-sm"
                        style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
                      />
                    </label>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                    <CartesianGrid stroke="#2a2a2a" />
                    <XAxis
                      dataKey="time"
                      stroke="#888888"
                      tick={{ fontSize: 11 }}
                      angle={-30}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis yAxisId="soc" domain={[0, 100]} stroke="#ffc20e" tick={{ fontSize: 11 }} label={{ value: "SoC (%)", angle: -90, position: "insideLeft", fill: "#ffc20e" }} />
                    <YAxis yAxisId="odo" orientation="right" stroke="#22c55e" tick={{ fontSize: 11 }} label={{ value: "Odometer (km)", angle: 90, position: "insideRight", fill: "#22c55e" }} />
                    <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #2a2a2a" }} labelStyle={{ color: "#ffffff" }} />
                    <Legend wrapperStyle={{ color: "#aaaaaa" }} />
                    {packetLossEvents.map((event, idx) => (
                      <ReferenceArea
                        key={idx}
                        yAxisId="soc"
                        x1={event.startTime[packetLossSortBy]}
                        x2={event.endTime[packetLossSortBy]}
                        stroke="none"
                        fill="#ef4444"
                        fillOpacity={0.18}
                        ifOverflow="visible"
                      />
                    ))}
                    {packetLossSortBy === "tcu" && rejectedRowsByTcu.map((r, idx) => (
                      <ReferenceLine
                        key={idx}
                        yAxisId="soc"
                        x={formatIst(r.tcuPacketTimeUtc)}
                        stroke="#f59e0b"
                        strokeDasharray="3 3"
                        ifOverflow="visible"
                        label={{ value: "rejected", position: "top", fill: "#f59e0b", fontSize: 10 }}
                      />
                    ))}
                    <Line yAxisId="soc" type="monotone" dataKey="soc" name="SoC (%)" stroke="#ffc20e" dot={false} connectNulls />
                    <Line yAxisId="odo" type="monotone" dataKey="odometerKm" name="Odometer (km)" stroke="#22c55e" dot={false} connectNulls />
                    <Brush dataKey="time" height={24} stroke="#ffc20e" fill="#1c1c1c" travellerWidth={8} />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs mt-2" style={{ color: "#555555" }}>
                  Shaded red bands mark detected packet loss. Drag the handles below the chart to zoom into a time range.
                </p>
              </div>
            )}

            {/* Packet loss events */}
            {successCount > 0 && (
              <div className="rounded-2xl p-6" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                <p className="text-sm font-semibold mb-4" style={{ color: "#ffffff" }}>Packet Loss Events</p>
                {packetLossEvents.length > 0 ? (
                  <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #2a2a2a" }}>
                    <DataTable
                      columns={packetLossColumns}
                      data={packetLossEvents}
                      pagination
                      paginationPerPage={10}
                      customStyles={tableStyles}
                      highlightOnHover
                      responsive
                      dense
                    />
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: "#555555" }}>No packet loss detected at the current thresholds.</p>
                )}
              </div>
            )}

            {/* Rejected / error packets */}
            {errorRows && errorRows.length > 0 && (
              <div className="rounded-2xl p-6" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                <p className="text-sm font-semibold mb-4" style={{ color: "#ffffff" }}>Rejected / Error Packets</p>
                <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #2a2a2a" }}>
                  <DataTable
                    columns={errorColumns}
                    data={errorRows}
                    pagination
                    paginationPerPage={10}
                    customStyles={tableStyles}
                    highlightOnHover
                    responsive
                    dense
                  />
                </div>
              </div>
            )}

            {/* Rides (trips) */}
            {successCount > 0 && (
              <div className="rounded-2xl p-6" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <p className="text-sm font-semibold" style={{ color: "#ffffff" }}>Rides (Trips)</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Ride sort by
                      <select
                        value={rideSortBy}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRideSortBy(e.target.value);
                          setRideSortByOpp(value === "tcu" ? "server" : "tcu");
                          setSelectedTripKey(null);
                        }}
                        className="rounded-md px-2 py-1 text-sm"
                        style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
                      >
                        <option value="tcu">TCU time</option>
                        <option value="server">Server time</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Gap threshold (min)
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={gapThresholdMin}
                        onChange={(e) => setGapThresholdMin(Number(e.target.value) || 0)}
                        className="w-16 rounded-md px-2 py-1 text-sm"
                        style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Min distance (km)
                      <input
                        type="number"
                        min="0"
                        step="0.05"
                        value={minTripDistanceKm}
                        onChange={(e) => setMinTripDistanceKm(Number(e.target.value) || 0)}
                        className="w-16 rounded-md px-2 py-1 text-sm"
                        style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#aaaaaa" }}>
                      Min duration (s)
                      <input
                        type="number"
                        min="0"
                        step="5"
                        value={minTripDurationSec}
                        onChange={(e) => setMinTripDurationSec(Number(e.target.value) || 0)}
                        className="w-16 rounded-md px-2 py-1 text-sm"
                        style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
                      />
                    </label>
                  </div>
                </div>

                {trips.length > 0 ? (
                  <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #2a2a2a" }}>
                    <DataTable
                      columns={tripColumns}
                      data={trips}
                      pagination
                      paginationPerPage={10}
                      customStyles={tableStyles}
                      highlightOnHover
                      responsive
                      dense
                      pointerOnHover
                      onRowClicked={(row) => setSelectedTripKey(tripKey(row))}
                    />
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: "#555555" }}>No rides detected at the current thresholds.</p>
                )}

                {selectedTrip && (
                  <div className="mt-4 rounded-2xl p-4" style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a" }}>
                    <p className="text-sm mb-3" style={{ color: "#dddddd" }}>
                      {selectedTrip.startTime[rideSortBy]} → {selectedTrip.endTime[rideSortBy]} · {fmtDuration(selectedTrip.durationSec)} · {selectedTrip.distanceKm.toFixed(2)} km
                      {" · SoC "}{fmtVal(selectedTrip.startSoc, "%")} → {fmtVal(selectedTrip.endSoc, "%")}
                    </p>
                    <div style={{ height: "420px" }}>
                      <RouteMap routePoints={selectedTripRoutePoints} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Table */}
            <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #2a2a2a" }}>
              <DataTable
                columns={columns}
                data={rows}
                pagination
                paginationPerPage={15}
                customStyles={tableStyles}
                highlightOnHover
                responsive
                dense
              />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default PacketDecodePage;
